import { useQuery } from '@tanstack/react-query';

import type { PlatformPermission } from '@/lib/permissions';

import {
  getPlatformSession,
  PLATFORM_SESSION_QUERY_KEY,
  type PlatformSession,
} from '../api/platform-session.graphql';

export interface PlatformPermissions {
  session: PlatformSession | undefined;
  isLoading: boolean;
  isError: boolean;
  /** `false` mientras carga o si falló: nunca se muestra una acción sin confirmar la capacidad. */
  can: (permission: PlatformPermission) => boolean;
  canAll: (...permissions: PlatformPermission[]) => boolean;
  refetch: () => void;
}

/**
 * OS-1717 — capacidades de la sesión. El rol se relee al volver a la pestaña y tras un 403 por
 * capacidad (`lib/api.ts`), así un cambio de rol se refleja sin cerrar sesión.
 */
export function usePlatformPermissions(): PlatformPermissions {
  const query = useQuery({
    queryKey: PLATFORM_SESSION_QUERY_KEY,
    queryFn: getPlatformSession,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: 'always',
  });
  const granted = new Set<string>(query.data?.permissions ?? []);
  const can = (permission: PlatformPermission): boolean => granted.has(permission);
  return {
    session: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    can,
    canAll: (...permissions) => permissions.every(can),
    refetch: () => void query.refetch(),
  };
}
